import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { requestOtp as apiRequestOtp, verifyOtp as apiVerifyOtp, type AuthCustomer } from "@/lib/api-client";

/**
 * Session client (VVS) côté SPA — remplace apps/web/lib/auth-session.ts +
 * lib/auth-actions.ts (cookies httpOnly lus/écrits via `next/headers` côté
 * serveur Next.js, inapplicable ici).
 *
 * Décision d'architecture de la tâche de scaffolding (auth) : une fois le
 * changement API-cookies en place (chantier parallèle), `POST /auth/otp/verify`
 * posera lui-même un cookie httpOnly sur `apps/api` — ce SPA ne lit ni ne
 * stocke JAMAIS le jeton d'accès/rafraîchissement lui-même. Ce contexte ne
 * garde que de l'état UI non sensible : "suis-je connecté ?" et les quelques
 * champs non sensibles du client (id, téléphone, prénom/nom) renvoyés dans le
 * corps JSON de la réponse de connexion.
 *
 * `verifyOtp()`/`requestOtp()` (./api-client.ts) restent, pour l'instant,
 * les mêmes endpoints qu'avant (le changement côté apps/api n'est pas encore
 * livré) : la réponse contient donc AUJOURD'HUI encore `accessToken`/
 * `refreshToken` en clair dans le JSON — ce module les ignore volontairement
 * (ne les stocke ni ne les lit nulle part), pour être déjà conforme au
 * contrat cible et fonctionner sans changement une fois le cookie httpOnly
 * réellement posé par l'API.
 *
 * Persistance `localStorage` : UNIQUEMENT un indice d'affichage ("j'étais
 * connecté en tant que ce client à la dernière visite"), jamais la source de
 * vérité — le cookie httpOnly (invisible depuis ce code) l'est. Un appel API
 * qui échoue en 401 doit être traité par l'appelant comme un signe de
 * déconnexion réelle (cf. `logout()` ci-dessous, à invoquer dans ce cas).
 */

const STORAGE_KEY = "vvs_customer_ui";

export type CustomerAuthUser = AuthCustomer;

export interface CustomerAuthContextValue {
  user: CustomerAuthUser | null;
  isLoggedIn: boolean;
  /**
   * Étape 1 du parcours OTP — ne modifie pas l'état de connexion.
   * Cf. `requestOtp()` (./api-client.ts) pour le contrat exact.
   */
  requestOtp: (phoneE164: string, email?: string) => Promise<{ message: string; devCode?: string }>;
  /** Étape 2 — en cas de succès, met à jour `user`/`isLoggedIn`. */
  verifyOtp: (phoneE164: string, code: string) => Promise<void>;
  /**
   * Efface uniquement l'état UI local. TODO(auth API) : apps/api n'expose pour
   * l'instant aucun endpoint `POST /auth/logout` capable d'invalider le
   * cookie httpOnly côté serveur (apps/web actuel se contente lui aussi de
   * supprimer ses propres cookies, cf. lib/auth-actions.ts `logoutAction`) —
   * quand cet endpoint existera, l'appeler ici avant de nettoyer l'état local.
   */
  logout: () => void;
}

const CustomerAuthContext = createContext<CustomerAuthContextValue | null>(null);

function readStoredUser(): CustomerAuthUser | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CustomerAuthUser;
  } catch {
    return null;
  }
}

function writeStoredUser(user: CustomerAuthUser | null): void {
  try {
    if (user) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Stockage indisponible (navigation privée stricte, quota…) : sans gravité,
    // l'état reste seulement en mémoire pour cette visite.
  }
}

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  // Lazy init : lu une seule fois au montage, jamais pendant le rendu initial
  // d'une page boutique (cf. contrainte "pas d'API navigateur dans le chemin
  // de rendu initial" de la tâche de scaffolding — ce Provider est monté une
  // fois pour toute l'app, pas par page).
  const [user, setUser] = useState<CustomerAuthUser | null>(() => readStoredUser());

  const requestOtp = useCallback(async (phoneE164: string, email?: string) => {
    const res = await apiRequestOtp({ phoneE164, email });
    return { message: res.message, devCode: res.devCode };
  }, []);

  const verifyOtp = useCallback(async (phoneE164: string, code: string) => {
    const result = await apiVerifyOtp({ phoneE164, code });
    // Ne retient QUE le client (non sensible) — accessToken/refreshToken
    // délibérément ignorés, cf. commentaire de tête de fichier.
    setUser(result.customer);
    writeStoredUser(result.customer);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    writeStoredUser(null);
  }, []);

  const value = useMemo<CustomerAuthContextValue>(
    () => ({ user, isLoggedIn: user !== null, requestOtp, verifyOtp, logout }),
    [user, requestOtp, verifyOtp, logout],
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth(): CustomerAuthContextValue {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) {
    throw new Error("useCustomerAuth() doit être utilisé à l'intérieur de <CustomerAuthProvider>.");
  }
  return ctx;
}
